import { Button } from '@mui/material';
import FormHelperText from '@mui/material/FormHelperText';
import React, { useCallback, useContext, useEffect, useState } from 'react';
import { Controller } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import API from '../../api';
import { SnackbarContext } from '../../contexts';
import { FormInput } from './FormInput';

/**
 * react-hook-form과 함께 사용하는 FormAdditionalEvidence 컴포넌트
 * @param {Object} props
 * @param {Object} props.control - react-hook-form의 control 객체
 * @param {string} props.name - 필드 이름 (예: 'additionalEvidence')
 * @param {Object} props.error - 에러 객체
 * @param {boolean} props.disabled - 비활성화 여부
 * @param {boolean} props.readOnly - 읽기 전용 여부
 * @param {Object} props.style - 스타일 객체
 * @param {string} props.className - 클래스명
 */
export const FormAdditionalEvidence = (props) => {
  const { control, name = 'additionalEvidence', error, rules, disabled, readOnly, style, className } = props;
  const { t } = useTranslation();

  const addItem = (currentValue, onChange) => {
    const newItem = {
      title: '',
      file: '',
      reason: '',
    };
    onChange([...(currentValue || []), newItem]);
  };

  const removeItem = (index, currentValue, onChange) => {
    const newArray = [...(currentValue || [])];
    newArray.splice(index, 1);
    onChange(newArray);
  };

  const updateItem = (index, field, value, currentValue, onChange) => {
    const newArray = [...(currentValue || [])];
    if (!newArray[index]) {
      newArray[index] = { title: '', file: '', reason: '' };
    }
    newArray[index] = {
      ...newArray[index],
      [field]: value,
    };
    onChange(newArray);
  };

  return (
    <div className={className || ''} style={style || {}}>
      <Controller
        control={control}
        name={name}
        defaultValue={[]}
        rules={rules}
        render={({ field, fieldState }) => {
          const items = field.value || [];

          return (
            <div className='flex flex-col gap-4'>
              {items.map((item, index) => (
                <div
                  key={`additional-evidence-${index}`}
                  className='border border-gray-200 border-solid rounded-lg p-6 bg-white shadow-sm'
                >
                  <div className='flex items-center justify-between mb-4'>
                    <h3 className='text-base font-semibold text-gray-800'>
                      {t('ADDITIONAL_DOC') || '추가 서류'} {index + 1}
                    </h3>
                    {!disabled && !readOnly && (
                      <Button
                        variant='outlined'
                        color='error'
                        size='small'
                        onClick={() => removeItem(index, field.value, field.onChange)}
                      >
                        {t('DELETE') || '삭제'}
                      </Button>
                    )}
                  </div>

                  <div className='flex flex-col gap-4'>
                    <FormInput
                      label={t('TITLE') || '제목'}
                      value={item.title || ''}
                      onChange={(e) => updateItem(index, 'title', e.target.value, field.value, field.onChange)}
                      placeholder={t('TITLE') || '제목을 입력하세요'}
                      disabled={disabled || readOnly}
                      required
                    />

                    <AdditionalEvidenceFileUploader
                      label={t('FILE') || '파일'}
                      value={item.file || ''}
                      onChange={(fileUrl) => updateItem(index, 'file', fileUrl, field.value, field.onChange)}
                      disabled={disabled || readOnly}
                      readOnly={readOnly}
                      index={index}
                    />

                    <FormInput
                      label={t('REASON') || '사유'}
                      value={item.reason || ''}
                      onChange={(e) => updateItem(index, 'reason', e.target.value, field.value, field.onChange)}
                      placeholder={t('REASON') || '사유를 입력하세요'}
                      disabled={disabled || readOnly}
                      multiline
                      rows={3}
                    />
                  </div>
                </div>
              ))}

              {!disabled && !readOnly && (
                <Button variant='outlined' onClick={() => addItem(field.value, field.onChange)} className='w-full'>
                  {t('ADD') || '추가'}
                </Button>
              )}

              {error && <FormHelperText error>{error.message}</FormHelperText>}
            </div>
          );
        }}
      />
    </div>
  );
};

/**
 * 추가 서류 파일 업로더 컴포넌트
 */
const AdditionalEvidenceFileUploader = ({ label, value, onChange, disabled, readOnly, index }) => {
  const { t } = useTranslation();
  const { updateSnackbar } = useContext(SnackbarContext);
  const [loading, setLoading] = useState(false);
  const [file, setFile] = useState(null);

  const handleFileChange = useCallback((e) => {
    const selectedFile = e.target.files?.[0];
    if (selectedFile) {
      setFile(selectedFile);
    }
  }, []);

  useEffect(() => {
    if (file) {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('path', 'project/additional-evidence');

      setLoading(true);
      API.Common.PostFile(formData)
        .then((res) => {
          const { cloudUrl } = res.data;
          onChange(cloudUrl);
          setLoading(false);
          const input = document.getElementById(`additional-evidence-file-${index}`);
          if (input) {
            input.value = '';
          }
          setFile(null);
        })
        .catch((err) => {
          console.error('File upload error:', err);
          setLoading(false);
          updateSnackbar({
            type: 'error',
            open: true,
            message: err.data?.message || '파일 업로드 중 오류가 발생했습니다.',
          });
          const input = document.getElementById(`additional-evidence-file-${index}`);
          if (input) {
            input.value = '';
          }
          setFile(null);
        });
    }
  }, [file, onChange, index, updateSnackbar]);

  const handleRemove = useCallback(() => {
    onChange('');
    const input = document.getElementById(`additional-evidence-file-${index}`);
    if (input) {
      input.value = '';
    }
  }, [onChange, index]);

  const renderPreview = () => {
    if (!value) {
      return (
        <div className='flex items-center justify-center w-full h-10 border-2 border-dashed border-gray-300 rounded-lg bg-gray-50'>
          <span className='text-sm text-gray-400'>{t('FILE_NOT_SELECTED') || '파일이 선택되지 않았습니다'}</span>
        </div>
      );
    }

    const fileName = value.split('/').pop();
    return (
      <div className='flex items-center justify-between w-full p-3 bg-gray-50 border border-gray-200 rounded-lg'>
        <span
          onClick={() => {
            window.open(value, '_blank');
          }}
          className='text-sm text-blue-600 hover:text-blue-800 w-full max-w-[240px] cursor-pointer text-ellipsis underline truncate flex-1'
        >
          {fileName}
        </span>
      </div>
    );
  };

  return (
    <div className='flex flex-col gap-1'>
      {label && <label className='text-sm font-medium text-gray-700 mb-1'>{label}</label>}
      <div className='flex flex-1 items-center gap-3'>
        <div className='relative flex-1'>
          {value && (
            <div className='absolute top-2 right-2 z-10'>
              {!disabled && !readOnly && (
                <button
                  type='button'
                  onClick={handleRemove}
                  className='flex items-center justify-center w-6 h-6 rounded-full bg-red-500 text-white hover:bg-red-600 transition-colors text-xs font-bold'
                  aria-label='Remove file'
                >
                  ✕
                </button>
              )}
            </div>
          )}
          {renderPreview()}
        </div>

        {!disabled && !readOnly && (
          <div className='flex justify-start'>
            <Button
              variant='contained'
              disableElevation
              color='primary'
              sx={{
                width: '100px',
                height: '40px',
              }}
              onClick={() => {
                document.getElementById(`additional-evidence-file-${index}`)?.click();
              }}
              disabled={loading}
            >
              {loading ? t('UPLOADING') || '업로드 중...' : t('FILE_CHOOSE') || '파일 선택'}
            </Button>
          </div>
        )}

        <div className='hidden'>
          <input
            type='file'
            id={`additional-evidence-file-${index}`}
            onChange={handleFileChange}
            disabled={loading || disabled || readOnly}
          />
        </div>
      </div>
    </div>
  );
};
